import type { User } from '~/models/User/User'
import type { Group } from '~/models/Group'
import type { SessionUserInfo } from '~/models/User/SessionUserInfo'
import type { GradeCode } from '~/models/Grade'
import type { SubjectCode } from '~/models/Subject'
import type { Organization } from '~/models/Organization'
import type { ActiveUserFilter } from '~/models/User/ActiveUserFilter'
import { computed, ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { UserRole } from '~/models/User/UserRole'
import { PreferredLanguage } from '~/models/User/PreferredLanguage'
import { sortByGradeIndex, highSchoolAdultAndPrepGrades } from '~/utils/gradeSorter'
import { findGradesBySubject, findSubjectsByGrade } from '~/utils/subjectMetadata'
import { isExcludedSubject, sortBySubjectIndex } from '~/utils/subjectSorter'
import useUserApi from '~/api/userApi'
import useSubjectsStore from '~/stores/subjects'
import arrayUtils from '~/utils/arrayUtils'
import usePendo from '~/composables/usePendo'
import useProductStore from '~/stores/product'

export const useAuthStore = defineStore('auth', () => {
  const { getUser, postUser, postUserOrganization } = useUserApi()
  const { hasOnlyTrialProducts } = storeToRefs(useProductStore())
  const { currentSubject } = storeToRefs(useSubjectsStore())
  const { intersect, unique } = arrayUtils()
  const { initPendo, updatePendo } = usePendo()

  const user = ref<User | undefined>(undefined)
  const isLoading = ref(false)

  const username = computed(() => user.value?.username || '')
  const isAuthenticated = computed(() => !!user.value?.username)
  const hasGrades = computed(() => !!user.value?.grades.length)
  const hasSubjects = computed(() => !!user.value?.subjects.length)
  const hasPreferredLanguage = computed(() => !!user.value?.preferredLanguage)
  const userGrades = computed((): GradeCode[] => [...(user.value?.grades || [])].sort(sortByGradeIndex))
  const userReadOnlyGrades = computed((): GradeCode[] => user.value?.userData?.readOnlyGrades || [])
  const userSubjects = computed(() => (user.value?.subjects || []).sort(sortBySubjectIndex).filter(unique<SubjectCode>))
  const userOrganization = computed(() => user.value?.organization)
  const userOrganizations = computed((): Organization[] => user.value?.userData?.schoolOrganizations || [])
  const needsOrganization = computed(() => !hasOrganization.value && hasMultipleOrganizations.value)
  const hasMultipleOrganizations = computed(() => userOrganizations.value.length > 1)
  const userPreferredLanguage = computed(() => user.value?.preferredLanguage || PreferredLanguage.Bokmal)
  const userRole = computed(() => user.value?.userData.role || UserRole.Anonymous)
  const isTeacher = computed(() => user.value?.userData.role === UserRole.Teacher)
  const isStudent = computed(() => user.value?.userData.role === UserRole.Student)
  const hasOrganization = computed(() => !!user.value?.organization?.number)
  const activeUserFilters = computed((): ActiveUserFilter[] => user.value?.userData.activeUserFilters || [])
  const userId = computed(() => username.value)
  const isHighSchoolUser = computed(() => intersect(highSchoolAdultAndPrepGrades, userGrades.value).length > 0)

  const activeUserGroups = computed((): Group[] =>
    activeUserFilters.value
      .filter(({ group }) => !!group)
      .map(({ group }) => ({
        // @ts-ignore Temporary fix for groups not having subjects in production APIs (yet). Remove when fixed.
        subjects: [],
        ...group,
      }))
      .filter(({ ownerOrganizationNumber: orgNumber }) => orgNumber && orgNumber === userOrganization.value?.number)
  )

  const activeUserGroup = computed(() => {
    if (!activeUserGroups.value[0]) return null
    return activeUserGroups.value[0]
  })

  function setUserActiveGroup(group?: Group) {
    const sessionInfo = { ...sessionUserInfo.value }
    sessionInfo.activeUserFilters = group ? [{
      group: group,
      organizationNumber: String(userOrganization.value?.number),
      fromDate: undefined,
      toDate: undefined,
      grade: undefined,
      student: undefined,
    }] : []
    return updateUser(sessionInfo)
  }

  /**
   * Finds subjects that matches users' grades
   */
  const userSubjectsByGrades = computed((): SubjectCode[] => {
    return userGrades.value
    .flatMap(findSubjectsByGrade)
    .filter(unique)
    .filter((subjectCode) => !isExcludedSubject(subjectCode))
    .sort(sortBySubjectIndex)
  })

  /**
   * Finds grades that matchers users' subjects
   */
  const userGradesBySubjects = computed((): GradeCode[] => {
    return userSubjects.value
    .flatMap(findGradesBySubject)
    .filter(unique)
    .sort(sortByGradeIndex)
  })

  /**
   * Finds relevant subjects which is either
   * 1. Current subject page
   * 2. Subjects in their settings
   * 3. Subjects used in their school grades
   * 4. No subjects
   */
  const userRelevantSubjects = computed((): SubjectCode[] => {
    if (currentSubject.value) return [currentSubject.value]
    if (userSubjects.value.length) return userSubjects.value
    if (userSubjectsByGrades.value.length) return userSubjectsByGrades.value
    return []
  })

  /**
   * Finds relevant grades which is either
   *
   *  1. Grades in their settings
   *  2. Grades matching subjects
   *  3. No grades
   */
  const userRelevantGrades = computed((): GradeCode[] => {
    if (userGrades.value.length) return userGrades.value
    if (userGradesBySubjects.value.length) return userGradesBySubjects.value
    return []
  })

  /**
   * SessionUserInfo is the object used upstream
   */
  const sessionUserInfo = computed((): SessionUserInfo => ({
    grades: userGrades.value,
    subjects: userSubjects.value,
    preferredLanguage: userPreferredLanguage.value,
    termsOfServiceAccepted: true,
    activeUserFilters: activeUserFilters.value,
  }))

  async function acquireSession() {
    isLoading.value = true
    try {
      user.value = await getUser()
      sessionInterval()
    } catch (error) {
      user.value = undefined
      throw error
    } finally {
      isLoading.value = false
    }
  }

  function sessionInterval() {
    const router = useRouter()
    const seconds = parseInt(import.meta.env.VITE_OIDC_INTERVAL)
    const session = setInterval(async () => {
      try {
        await getUser()
      } catch (error) {
        clearInterval(session)
        user.value = undefined
        await router.push({
          name: 'login',
          query: { redirectUri: window.location.toString() },
        })
      }
    }, seconds*1000)
  }

  async function updateUser(sessionUser: SessionUserInfo) {
    if (!user.value) throw Error('Missing user')
    isLoading.value = true
    try {
      user.value.userData.activeUserFilters = sessionUser.activeUserFilters
      user.value = await postUser(sessionUser)
    } finally {
      isLoading.value = false
      updatePendo(user.value)
    }
  }

  async function updateUserOrganization(organization: Organization) {
    if (!user.value) throw Error('Missing user')
    isLoading.value = true
    try {
      user.value.organization = await postUserOrganization(organization)
    } finally {
      isLoading.value = false
      updatePendo(user.value)
    }
  }

  function toggleGrade(grade: GradeCode) {
    if (!user.value) throw Error('Missing user')
    let grades = [...user.value.grades]
    if (grades.includes(grade)) {
      grades = grades.filter((code) => code !== grade || isReadOnlyGrade(code))
    } else {
      grades.push(grade)
    }
    user.value = {
      ...user.value,
      grades,
    }
  }

  function toggleSubject(subject: SubjectCode) {
    if (!user.value) throw Error('Missing user')
    if (user.value.subjects.includes(subject)) {
      user.value.subjects = user.value.subjects.filter((code) => code !== subject)
      return
    }
    user.value.subjects.push(subject)
  }

  function hasSubject(subject: SubjectCode) {
    return userSubjects.value.includes(subject)
  }

  function hasGrade(grade: GradeCode) {
    return userGrades.value.includes(grade)
  }

  function isReadOnlyGrade(grade: GradeCode) {
    return userReadOnlyGrades.value.includes(grade)
  }

  function isPreferredLanguage(preferredLanguage: PreferredLanguage) {
    return preferredLanguage === user.value?.preferredLanguage
  }

  function setPreferredLanguage(preferredLanguage: PreferredLanguage) {
    if (!user.value) throw Error('Missing user')
    user.value.preferredLanguage = preferredLanguage
  }

  function setActiveOrganization(organization: Organization) {
    if (!user.value) throw Error('Missing user')
    user.value.organization = organization
  }

  watch(isAuthenticated, () => {
    if (user.value) {
      initPendo(user.value)
    }
  })

  watch(() => [isAuthenticated, user, hasOnlyTrialProducts], ([isAuthenticated, user, hasOnlyTrialProducts]) => {
    if (undefined === isAuthenticated.value) return
    if (undefined === user.value) return
    if (undefined === hasOnlyTrialProducts.value) return

    updatePendo(<User>user.value, <boolean>hasOnlyTrialProducts.value)
  }, { deep: true })

  return {
    user,
    username,
    isLoading,
    isAuthenticated,
    hasGrades,
    hasSubjects,
    hasPreferredLanguage,
    userGrades,
    userReadOnlyGrades,
    userSubjects,
    userRole,
    userOrganization,
    userOrganizations,
    needsOrganization,
    hasMultipleOrganizations,
    userPreferredLanguage,
    isTeacher,
    isStudent,
    hasOrganization,
    activeUserFilters,
    sessionUserInfo,
    acquireSession,
    updateUser,
    updateUserOrganization,
    toggleGrade,
    toggleSubject,
    hasSubject,
    hasGrade,
    isReadOnlyGrade,
    isPreferredLanguage,
    setPreferredLanguage,
    setActiveOrganization,
    userId,
    userRelevantSubjects,
    userSubjectsByGrades,
    userRelevantGrades,
    userGradesBySubjects,
    isHighSchoolUser,
    activeUserGroups,
    activeUserGroup,
    setUserActiveGroup,
  }
})

export default useAuthStore
